import Sentry from "@similarweb/sentry-logger";

import type {
    ICampaignLocationAndLanguage,
    ILocationDetailsToPresent,
    ISupportedLanguagesAndLocale,
    ISupportedLocationByGoogle,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import {
    forbiddenLanguages,
    forbiddenLocations,
    NON_VALID_LOCATION_TYPES,
    SORT_ORDER,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { fetchLocations, getLanguagesList } from "app/components/campaigns/campaigns-wizard/campaignsWizardFetch";
import { getParsedDataFromLocalStorage } from "app/components/widget/widget-utilities/Campaigns/CampaignsUtils";
import { RANK_TRACKER_LOCALE_CONFIGS_KEY } from "app/common/campaigns/KeywordsCampaignConsts";
import CountryService from "app/services/CountryService";

export const getLocationNameToPresent = (name: string): string => {
    return name?.replaceAll(",", ", ");
};

export const getLocationNameToSave = (name: string): string => {
    return name?.replaceAll(", ", ",");
};

export const getSortedLocations = (locations: ISupportedLocationByGoogle[] = []): ILocationDetailsToPresent[] => {
    return locations
        .reduce<ILocationDetailsToPresent[]>((result, location) => {
            const isValidLocationType = NON_VALID_LOCATION_TYPES.every(
                (nonValidLocation) => location.type.toString() !== nonValidLocation,
            );
            const isValidLocation = forbiddenLocations.every((forbiddenLocation) => {
                if (forbiddenLocation.includes("*")) {
                    const partialForbiddenLocation = forbiddenLocation.replace("*", "");
                    return !location.canonicalName.includes(partialForbiddenLocation);
                } else {
                    return location.canonicalName !== forbiddenLocation;
                }
            });
            const shouldAddLocation = isValidLocation && isValidLocationType;
            if (shouldAddLocation) {
                const selectedCountry = CountryService.countries.find(
                    (country) => location.countryCode.toLowerCase() === country.code,
                );
                result.push({
                    id: selectedCountry?.id,
                    text: location.canonicalName,
                    type: location.type,
                });
            }
            return result;
        }, [])
        .sort((a, b) => {
            return SORT_ORDER.indexOf(a.type) - SORT_ORDER.indexOf(b.type);
        });
};

export const getLanguagesForCampaigns = async (isDemoCampaign: boolean): Promise<ISupportedLanguagesAndLocale[]> => {
    let languages: ISupportedLanguagesAndLocale[] | undefined = getParsedDataFromLocalStorage(
        RANK_TRACKER_LOCALE_CONFIGS_KEY,
    );

    if (!languages) {
        const languagesList: ISupportedLanguagesAndLocale[] = await getLanguagesList(isDemoCampaign);
        const filteredLanguagesList = languagesList?.filter((language) =>
            forbiddenLanguages.every((forbiddenLanguage) => !language.description.includes(forbiddenLanguage)),
        );
        localStorage.setItem(RANK_TRACKER_LOCALE_CONFIGS_KEY, JSON.stringify(filteredLanguagesList));
        languages = filteredLanguagesList;
    }
    return languages;
};

export const getLocationsForCampaigns = async (isDemoCampaign: boolean): Promise<ILocationDetailsToPresent[]> => {
    try {
        const languagesList = await fetchLocations(isDemoCampaign);
        return getSortedLocations(languagesList);
    } catch (error) {
        Sentry.error(`Error fetching languages and locations from server -`, error);
    }
};

export const getLanguagesAndLocationsForCampaigns = async (
    isDemoCampaign: boolean,
): Promise<ICampaignLocationAndLanguage> => {
    const promises: Promise<ISupportedLanguagesAndLocale[] | ILocationDetailsToPresent[]>[] = [
        getLanguagesForCampaigns(isDemoCampaign),
        getLocationsForCampaigns(isDemoCampaign),
    ];
    const [languagesListResult, locationsResult] = await Promise.allSettled(promises);
    const languagesList = (languagesListResult as PromiseFulfilledResult<ISupportedLanguagesAndLocale[]>)?.value;
    const locationsList = (locationsResult as PromiseFulfilledResult<ILocationDetailsToPresent[]>)?.value;
    return {
        languagesList,
        locationsList,
    };
};
